import React, { useCallback, useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Wallet, AlertCircle } from "lucide-react";

/**
 * Current stock valuation block: Total Asset Value (network) + per-site breakdown.
 * Unrated items are surfaced explicitly so a low total never quietly hides a
 * missing rate.
 */
export default function AssetValueBlock({ refreshTick }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/inventory/asset-value");
      setData(r.data);
    } catch (e) {
      setData(null);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load, refreshTick]);

  if (loading) {
    return (
      <section className="bg-white border border-slate-200 rounded-2xl p-5">
        <div className="text-sm text-slate-500">Loading asset value…</div>
      </section>
    );
  }
  if (!data) return null;

  const sites = data.per_site || [];
  const unratedCount = data.unrated_item_count || 0;
  const hasValue = (data.network_total_value || 0) > 0 || sites.length > 0;

  return (
    <section
      data-testid="inv-asset-value-block"
      className="bg-white border border-slate-200 rounded-2xl p-5 lg:p-6"
    >
      <div className="flex flex-col lg:flex-row lg:items-center lg:gap-6 gap-3">
        <div className="flex items-center gap-3 lg:flex-1">
          <div className="w-12 h-12 rounded-2xl bg-olive/10 grid place-items-center text-olive shrink-0">
            <Wallet size={20} strokeWidth={1.8} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[11px] font-medium uppercase tracking-wider text-slate-500">
              Total asset value · network
            </div>
            <div className="text-3xl lg:text-4xl font-semibold tracking-tight text-slate-900 tabular-nums mt-0.5" data-testid="asset-value-network">
              {fmtINR(data.network_total_value)}
            </div>
            <div className="text-[11px] text-slate-500 mt-0.5">
              {data.network_total_qty} total units · {data.rated_item_count} item{data.rated_item_count === 1 ? "" : "s"} valued
            </div>
          </div>
        </div>

        {unratedCount > 0 && (
          <div
            data-testid="asset-value-unrated-banner"
            className="rounded-xl bg-amber-50 border border-amber-200 px-3 py-2.5 inline-flex items-start gap-2 max-w-md"
            title={data.unrated_items?.map((u) => u.name).join(", ")}
          >
            <AlertCircle size={14} strokeWidth={2} className="text-amber-700 shrink-0 mt-0.5" />
            <div className="text-[11px] text-amber-800 leading-relaxed">
              <span className="font-semibold">{unratedCount} item{unratedCount === 1 ? "" : "s"} unrated</span> — excluded from value.
              Set a rate from the Items tab so they count.
            </div>
          </div>
        )}
      </div>

      {hasValue && sites.length > 0 && (
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5" data-testid="asset-value-per-site">
          {sites.map((s) => (
            <div
              key={s.site}
              data-testid={`asset-site-${s.site}`}
              className="rounded-xl border border-slate-100 bg-slate-50/60 px-3 py-2.5"
            >
              <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 truncate" title={s.site}>
                {s.site}
              </div>
              <div className="text-base font-semibold text-slate-900 tabular-nums mt-0.5">
                {fmtINR(s.value)}
              </div>
              <div className="text-[10px] text-slate-400 mt-0.5">
                {s.qty} units
                {s.unrated_qty > 0 && (
                  <span className="ml-1 text-amber-700 font-medium">
                    · {s.unrated_qty} unrated
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {!hasValue && (
        <div className="mt-3 text-xs text-slate-500" data-testid="asset-value-empty">
          No stock recorded yet. Add an item with opening stock to see network valuation.
        </div>
      )}
    </section>
  );
}

function fmtINR(n) {
  if (n == null) return "—";
  return `₹${Number(n).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
